import { useState } from "react";
import { Download, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatFileSize, formatItalianDateTime } from "@/lib/format";
import { Lightbox } from "./Lightbox";

const ASPECT_META: Record<string, { label: string; classes: string; use: string }> = {
  "1:1": {
    label: "1:1 Square",
    classes: "aspect-square",
    use: "Feed IG/FB · Email hero",
  },
  "4:5": {
    label: "4:5 Portrait",
    classes: "aspect-[4/5]",
    use: "Post IG · Portrait",
  },
  "9:16": {
    label: "9:16 Story",
    classes: "aspect-[9/16]",
    use: "Story IG/FB · Reels",
  },
};

export function FlyerPreview({
  aspect,
  url,
  filename,
  bytes,
  modifiedAt,
}: {
  aspect: "1:1" | "4:5" | "9:16";
  url: string;
  filename: string;
  bytes?: number;
  modifiedAt?: string;
}) {
  const [open, setOpen] = useState(false);
  const meta = ASPECT_META[aspect] ?? ASPECT_META["1:1"];

  return (
    <div className="flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
        <div>
          <div className="text-xs font-semibold text-slate-900">
            {meta.label}
          </div>
          <div className="text-[10px] text-slate-500">{meta.use}</div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setOpen(true)}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
            aria-label="Ingrandisci"
            title="Ingrandisci"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
          <a
            href={url}
            download={filename}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
            aria-label="Scarica"
            title="Scarica PNG"
          >
            <Download className="h-4 w-4" />
          </a>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "relative block w-full overflow-hidden bg-slate-100 transition-opacity hover:opacity-95",
          meta.classes,
        )}
      >
        <img
          src={url}
          alt={`Locandina ${aspect}`}
          className="h-full w-full object-contain"
          loading="lazy"
        />
      </button>

      <div className="flex items-center justify-between border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-500">
        <span className="truncate font-mono" title={filename}>
          {filename}
        </span>
        {bytes ? <span className="shrink-0">{formatFileSize(bytes)}</span> : null}
      </div>

      <Lightbox
        open={open}
        src={url}
        alt={`Locandina ${aspect}`}
        caption={`${meta.label} · ${filename}${modifiedAt ? ` · ${formatItalianDateTime(modifiedAt)}` : ""}`}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}
